
import { StatsCards } from "./StatsCards";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, startOfMonth, endOfMonth } from "date-fns";
import { StatisticsCharts } from "@/components/history/charts/StatisticsCharts";
import { TransactionCharts } from "@/components/history/TransactionCharts";
import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";

interface BusinessType {
  id: string;
  name: string;
  description?: string;
}

interface OwnerData {
  business_type_id: string;
  company_name: string;
  created_at: string;
  currency: string;
  owner_id: string;
  business_types: BusinessType;
}

interface InterfaceModel {
  id: string;
  name: string;
  description?: string;
  layout: Record<string, any>;
  business_types: {
    id: string;
    name: string;
  };
}

interface OverviewProps {
  ownerData: OwnerData;
  interfaceModel: InterfaceModel;
  sampleData: any[];
}

export const Overview = ({ ownerData, interfaceModel, sampleData }: OverviewProps) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { data: currency } = useOwnerCurrency();

  const { data: todayTransactions } = useQuery({
    queryKey: ["today_transactions"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const startOfToday = startOfDay(new Date());
      
      const { data, error } = await supabase
        .from("poultry_transactions")
        .select(`
          *,
          stock_items (
            data,
            stock_id
          )
        `)
        .eq("created_by", user.id)
        .gte("created_at", startOfToday.toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["overview_stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const currentDate = new Date();
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);

      const { data: monthlyStats, error: monthlyError } = await supabase
        .from("poultry_transactions")
        .select("total_amount")
        .eq("created_by", user.id)
        .eq("transaction_type", "sale")
        .eq("is_deducted_from_revenue", true)
        .gte("created_at", monthStart.toISOString())
        .lte("created_at", monthEnd.toISOString());

      if (monthlyError) throw monthlyError;

      const { data: yearlyStats, error: yearlyError } = await supabase
        .from("poultry_transactions")
        .select("total_amount")
        .eq("created_by", user.id)
        .eq("transaction_type", "sale")
        .eq("is_deducted_from_revenue", true)
        .gte("created_at", new Date(currentDate.getFullYear(), 0, 1).toISOString())
        .lte("created_at", new Date(currentDate.getFullYear(), 11, 31).toISOString());

      if (yearlyError) throw yearlyError;

      const monthlyTotal = monthlyStats?.reduce((sum, record) => sum + (record.total_amount || 0), 0) || 0;
      const yearlyTotal = yearlyStats?.reduce((sum, record) => sum + (record.total_amount || 0), 0) || 0;

      console.log("Statistiques mensuelles:", monthlyStats);
      console.log("Total mensuel:", monthlyTotal);
      
      return {
        validatedTotal: monthlyTotal,
        yearlyTotal: yearlyTotal
      };
    }
  });

  return (
    <div className="space-y-6">
      <div className="w-full overflow-hidden">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Note : Les chiffres affichés ici correspondent uniquement aux transactions qui ont été validées depuis l'onglet "Historique".
              </p>
            </div>
          </div>
        </div>
        <StatsCards />
      </div>
      
      <StatisticsCharts
        validatedTotal={stats?.validatedTotal}
        lastMonthStats={0}
        yearlyTotal={stats?.yearlyTotal}
        customPeriodTotal={0}
        startDate={startDate}
        endDate={endDate}
        currency={currency}
      />

      <Card className="p-2 md:p-6">
        {todayTransactions && <TransactionCharts transactions={todayTransactions} />}
      </Card>
    </div>
  );
};
